import { memo, forwardRef, useImperativeHandle, useRef } from 'react';

import { Stage } from 'react-konva';

import { useStageSizing } from './hooks';
import useMagicPenCanvas from './useMagicPenCanvas';

import { IMagicPenCanvasRef } from '@src/types';

import style from '../magicPen.module.scss';
import { calculateResponsiveFontSize } from '../helpers/bounds.helpers';

interface IMagicPenCanvasProps {
  canAnnotate: boolean;
}

const MagicPenCanvas = forwardRef<IMagicPenCanvasRef, IMagicPenCanvasProps>(({ canAnnotate }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageDimensions = useStageSizing(containerRef);

  const {
    activeTool,
    stageRef,
    handleMouseDown,
    handleMouseUp,
    handleStageClick,
    handleMouseMoveWithTooltip,
    renderLayer,
    editingStickyNote,
    handleTextEditComplete,
    handleTextEditCancel,
    handleTextSelection,
    textareaRef,
    textareaValue,
    setTextareaValue,
  } = useMagicPenCanvas(canAnnotate, stageDimensions.width, stageDimensions.height);

  // Expose the stageRef through the ref
  useImperativeHandle(ref, () => ({
    stageRef,
  }));

  return (
    <div
      className={`${style.magicPenCanvasContainer} ${activeTool === 'eraser' ? style.eraserMode : ''}`}
      ref={containerRef}
    >
      <Stage
        width={stageDimensions.width}
        height={stageDimensions.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMoveWithTooltip}
        onMouseUp={handleMouseUp}
        onClick={handleStageClick}
        ref={stageRef}
      >
        {renderLayer()}
      </Stage>

      {/* Text editing overlay: added an HTML <textarea> element that appears over the Konva canvas when editing because
      Konva.Text doesn't have a native editable prop that actually makes text editable */}
      {editingStickyNote && canAnnotate && (
        <textarea
          ref={textareaRef}
          className={style.stickyNoteTextarea}
          value={textareaValue}
          onChange={(e) => {
            setTextareaValue(e.target.value);
          }}
          onSelect={(e) => {
            const target = e.target as HTMLTextAreaElement;
            const start = target.selectionStart || 0;
            const end = target.selectionEnd || 0;

            if (start !== end) {
              // Text is selected
              handleTextSelection(start, end);
            } else {
              // No text selected
              handleTextSelection(null, null);
            }
          }}
          onBlur={(e) => {
            const relatedTarget = e.relatedTarget as HTMLElement | null;

            const isInsidePalette =
              relatedTarget?.closest('#magic-pen-color-palette-trigger') ||
              relatedTarget?.closest('#magic-pen-color-palette-popup');

            if (!isInsidePalette) {
              handleTextEditComplete(textareaValue);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleTextEditComplete(textareaValue);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleTextEditCancel();
            }
          }}
          style={{
            left: editingStickyNote.x,
            top: editingStickyNote.y,
            width: editingStickyNote.width,
            height: editingStickyNote.height,
            fontSize: `${calculateResponsiveFontSize(editingStickyNote.fontSize || 14, stageDimensions.width, stageDimensions.height)}px`,
            color: editingStickyNote.textColor || '#000000',
            // Add responsive padding that scales with font size
            padding: `${calculateResponsiveFontSize((editingStickyNote.fontSize || 14) * 0.3, stageDimensions.width, stageDimensions.height)}px`,
          }}
        />
      )}
    </div>
  );
});

export default memo(MagicPenCanvas);
